import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  UpdateWarehouseMutation,
  WarehouseAreaMutationUpdateArgs,
  WarehouseAreaUpdateInput,
} from "@/graphql/codegen/graphql"
import { UpdateWarehouseDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateWarehouseProps {
  errorMessage?: string
  successMessage?: string
  onSuccess: () => void
}

interface UseUpdateWarehouseReturn {
  update: (input: WarehouseAreaUpdateInput) => Promise<void>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateWarehouse = ({
  errorMessage,
  successMessage,
  onSuccess,
}: UseUpdateWarehouseProps): UseUpdateWarehouseReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<UpdateWarehouseMutation, WarehouseAreaMutationUpdateArgs>(
    UpdateWarehouseDocument
  )

  const update = async (input: WarehouseAreaUpdateInput) => {
    const { data, error } = await executeMutation({ input })
    const hasError = error || data?.warehouseArea.update.__typename === "WarehouseAreaUpdateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message: error?.message ?? errorMessage ?? "Failed to update warehouse.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: successMessage ?? "Warehouse successfully updated.",
    })
    onSuccess()
  }

  return {
    update,
    isUpdating: fetching,
    error: error,
  }
}
